import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowRight, ExternalLink } from 'lucide-react';

const InsightsPage = () => {
  const articles = [
    {
      id: 'virtual-autism',
      title: 'The "Virtual Autism" Epidemic: Are We Diagnosing a Neurotype or Environmental Trauma?',
      abstract: 'Walk into any pediatric OPD in urban India today, and you will see a chillingly identical waiting room. Are we looking at authentic, lifelong neurodivergence, or are we looking at a temporary, environmentally induced neuro-deprivation?',
      slug: 'virtual-autism-epidemic'
    },
    {
      id: 'masking-bill',
      title: 'The Masking Bill: Why "Model Students" Collapse at Home',
      abstract: 'The phenomenon known as "After-School Restraint Collapse" is frequently misdiagnosed. However, a rigorous neuropsychological audit suggests a far more complex metabolic reality: the physiological debt incurred by neurodivergent children masking in neuro-normative environments.',
      slug: 'masking-bill-model-students'
    },
    {
      id: 'diagnostic-blind-spot',
      title: 'The Diagnostic Blind Spot: Measuring Internal Distress vs. External Disruption',
      abstract: 'In neurodevelopmental diagnostics, the child who slips through the cracks isn\'t the one struggling to sit still. It is the "perfect" student. Exploring the autonomic nervous system cost of extreme camouflaging.',
      slug: 'diagnostic-blind-spot'
    },
    {
      id: 'algorithmic-pathologizing',
      title: 'Algorithmic Pathologizing: How the Digital Crisis Teaches Youth to Conceal',
      abstract: 'We frequently discuss how 60-second videos lead to rampant misdiagnosis among adolescents. But a deeper, darker secondary layer is emerging: The algorithm is now actively teaching our youth to mask their distress from clinicians.',
      slug: 'algorithmic-pathologizing'
    }
  ];

  return (
    <div>
      {/* Header Section */}
      <section className="section" style={{ paddingTop: '6rem', paddingBottom: '3rem' }} data-testid="insights-hero">
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '900px', margin: '0 auto' }}>
            <p className="uppercase-small" style={{ marginBottom: '1rem' }}>
              INSIGHTS & CLINICAL BRIEFS
            </p>
            <h1 style={{ marginBottom: '1.5rem' }}>
              Editorial Articles
            </h1>
            <p style={{ 
              fontSize: '1.25rem',
              lineHeight: 1.8,
              color: 'var(--text-muted)',
              fontWeight: 500
            }}>
              Deep-dive clinical perspectives on contemporary neurodevelopmental challenges.
            </p>
          </div>
        </div>
      </section>

      {/* Editorial Articles Grid - Tier 2 */}
      <section className="section" data-testid="editorial-articles">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <BookOpen size={48} color="var(--accent)" style={{ margin: '0 auto 1rem' }} />
            <h2>Clinical Briefs & Editorial Articles</h2>
            <p style={{ 
              fontSize: '1.125rem',
              color: 'var(--text-muted)',
              maxWidth: '700px',
              margin: '1rem auto 0'
            }}>
              Examining the intersection of neurodevelopmental science, clinical practice, and modern culture.
            </p>
          </div>

          {/* 2-Column Article Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '2rem',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            {articles.map((article, index) => (
              <div 
                key={article.id}
                className="glass-card"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '2.5rem',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{
                  display: 'inline-block',
                  background: 'var(--accent)',
                  color: 'white',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  marginBottom: '1.5rem',
                  width: 'fit-content',
                  letterSpacing: '0.05em'
                }}>
                  CLINICAL BRIEF
                </div>

                <h3 style={{
                  fontSize: '1.5rem',
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 600,
                  lineHeight: 1.3,
                  color: 'var(--primary)',
                  marginBottom: '1rem'
                }}>
                  {article.title}
                </h3>

                <p style={{
                  fontSize: '1rem',
                  lineHeight: 1.8,
                  color: 'var(--text-main)',
                  marginBottom: '2rem',
                  flex: 1,
                  fontStyle: 'italic'
                }}>
                  {article.abstract}
                </p>

                <Link
                  to={`/insights/${article.slug}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.875rem 1.5rem',
                    background: 'transparent',
                    border: '2px solid var(--accent)',
                    borderRadius: '8px',
                    color: 'var(--accent)',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    textDecoration: 'none',
                    transition: 'all 0.3s ease',
                    width: 'fit-content'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = 'var(--accent)';
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'var(--accent)';
                  }}
                >
                  Read Clinical Brief
                  <ArrowRight size={18} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-alt" data-testid="insights-cta">
        <div className="container">
          <div style={{ 
            maxWidth: '800px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <h2 style={{ marginBottom: '1rem' }}>
              Stay Updated
            </h2>
            <p style={{ 
              fontSize: '1.125rem',
              lineHeight: 1.8,
              color: 'var(--text-muted)',
              marginBottom: '2rem'
            }}>
              For regular clinical insights, research updates, and evidence-based guidance, follow along on Instagram or subscribe to our newsletter.
            </p>
            <a
              href="https://www.instagram.com/isharastogipsychology/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '1.125rem',
                padding: '1rem 2rem'
              }}
            >
              Follow on Instagram
              <ExternalLink size={20} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InsightsPage;
